import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { toast } from "sonner";
import {
  AlertTriangle, BarChart3, Boxes, ChefHat, ChevronRight, ClipboardList, Factory, FileSpreadsheet,
  LogOut, Menu, RefreshCw, Settings2, TrendingUp, Truck,
} from "lucide-react";
import { API } from "../../lib/apiClient";
import logoIcon from "../../assets/logo_icon.png";
import { NavButton, ConfirmModal, sectionTitle } from "./Shared";
import { Overview } from "./Overview";
import { InventoryView } from "./InventoryView";
import { DispatchView } from "./Dispatch";
import { ActivityView } from "./ActivityView";
import { TrendsView } from "./Trends";
import { ReportsView } from "./Reports";
import { KitchenRequisitionsView } from "./KitchenRequisitions";
import { SettingsView } from "./Settings";
import { ItemModal } from "./modals/ItemModal";
import { ImportModal } from "./modals/ImportModal";
import { StockModal } from "./modals/StockModal";
import { ChallanModal } from "./modals/ChallanModal";
import { PrintChallan } from "./PrintChallan";

export function Dashboard({ auth, onLogout }) {
  const canEdit = !!auth.can_edit;
  const [section, setSection] = useState("dashboard");
  const [inventory, setInventory] = useState([]);
  const [challans, setChallans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [business, setBusiness] = useState({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [printChallan, setPrintChallan] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    try {
      const [items, statsRes, challanRes, txnRes, businessRes, contactsRes, requisitionRes] = await Promise.all([
        axios.get(`${API}/inventory`), axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/challans`), axios.get(`${API}/transactions`),
        axios.get(`${API}/business`), axios.get(`${API}/contacts`),
        axios.get(`${API}/requisitions`),
      ]);
      setInventory(items.data); setStats(statsRes.data); setChallans(challanRes.data);
      setTransactions(txnRes.data); setBusiness(businessRes.data); setContacts(contactsRes.data);
      setRequisitions(requisitionRes.data);
    } catch (error) { toast.error("Could not load plant data"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return inventory.filter((item) => {
      const isArchived = !!item.archived;
      if (filter === "Archived") { if (!isArchived) return false; }
      else {
        if (isArchived) return false;
        if (filter !== "All" && item.category !== filter) return false;
      }
      return `${item.name} ${item.sku} ${item.batch_number || ""}`.toLowerCase().includes(search.toLowerCase());
    });
  }, [inventory, filter, search]);
  const activeInventory = useMemo(() => inventory.filter((i) => !i.archived), [inventory]);
  const rawMaterialItems = useMemo(() => activeInventory.filter((i) => i.category === "Raw Material"), [activeInventory]);
  const lowStock = activeInventory.filter((item) => item.quantity <= item.min_threshold);
  const go = (next) => { setSection(next); setModal(null); setMobileOpen(false); };

  const deleteItem = async (item) => {
    try { await axios.delete(`${API}/inventory/${item.id}`); toast.success("Item permanently deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete item"); }
    finally { setConfirmAction(null); }
  };
  const deleteChallan = async (c) => {
    try { await axios.delete(`${API}/challans/${c.id}`); toast.success(`${c.challan_no} deleted`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete challan"); }
    finally { setConfirmAction(null); }
  };
  const deleteRequisition = async (r) => {
    try { await axios.delete(`${API}/requisitions/${r.id}`); toast.success(`${r.req_no} deleted and stock restored`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete requisition"); }
    finally { setConfirmAction(null); }
  };

  return <div className="app-shell"><Toaster position="top-right" richColors />
    <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="brand"><div className="brand-mark"><img src={logoIcon} alt="Surya's Exports" /></div><div><strong>Surya's</strong><span>Exports Pvt Ltd</span></div></div>
      <div className="plant-chip"><span className="status-dot" /> <span data-testid="plant-status">Plant online</span><button data-testid="refresh-data-button" aria-label="Refresh data" onClick={load}><RefreshCw size={14} /></button></div>
      <nav className="nav-list" aria-label="Main navigation">
        <p className="nav-label">Operations</p>
        <NavButton active={section === "dashboard"} onClick={() => go("dashboard")} icon={<BarChart3 size={18} />} label="Overview" testid="nav-overview" />
        <NavButton active={section === "inventory"} onClick={() => go("inventory")} icon={<Boxes size={18} />} label="Inventory" badge={stats.low_stock_count} testid="nav-inventory" />
        <NavButton active={section === "dispatch"} onClick={() => go("dispatch")} icon={<Truck size={18} />} label="Dispatch desk" testid="nav-dispatch" />
        <NavButton active={section === "activity"} onClick={() => go("activity")} icon={<ClipboardList size={18} />} label="Stock activity" testid="nav-activity" />
        <NavButton active={section === "kitchen"} onClick={() => go("kitchen")} icon={<ChefHat size={18} />} label="Kitchen Usage" testid="nav-kitchen" />
        <NavButton active={section === "trends"} onClick={() => go("trends")} icon={<TrendingUp size={18} />} label="Trends" testid="nav-trends" />
        <NavButton active={section === "reports"} onClick={() => go("reports")} icon={<FileSpreadsheet size={18} />} label="Reports" testid="nav-reports" />
        <p className="nav-label nav-label-bottom">Workspace</p>
        <NavButton active={section === "settings"} onClick={() => go("settings")} icon={<Settings2 size={18} />} label="Plant details" testid="nav-settings" />
      </nav>
      <div className="sidebar-footer">
        <div className="avatar">{auth.username.slice(0, 2).toUpperCase()}</div>
        <div><strong data-testid="sidebar-username">{auth.role_label}</strong><span>{canEdit ? "Full access" : "Add-only access"}</span></div>
        <button className="logout-button" data-testid="logout-button" title="Log out" aria-label="Log out" onClick={onLogout}><LogOut size={15} /></button>
      </div>
    </aside>
    <main className="main-content">
      <header className="topbar"><button className="mobile-menu" data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle navigation"><Menu size={21} /></button><div className="crumb"><Factory size={16} /><span>{business.company_name || "Surya's Exports"}</span><ChevronRight size={14} /><b>{sectionTitle(section)}</b></div><div className="top-actions"><span className="today">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span><button className="icon-button" data-testid="header-alerts-button" onClick={() => go("inventory")} aria-label="View low stock alerts"><AlertTriangle size={18} /><i>{stats.low_stock_count || 0}</i></button></div></header>
      {section === "dashboard" && <Overview stats={stats} lowStock={lowStock} challans={challans} go={go} loading={loading} onPrint={setPrintChallan} roleLabel={auth.role_label} />}
      {section === "inventory" && <InventoryView items={filtered} inventory={inventory} filter={filter} setFilter={setFilter} search={search} setSearch={setSearch} canEdit={canEdit} open={(type, item) => { setSelected(item); setModal(type); }} onAdd={() => { setSelected(null); setModal("new-item"); }} onImport={() => setModal("import")} onDelete={(item) => setConfirmAction({ type: "item", item })} onArchive={async (item) => {
        try {
          const payload = { name: item.name, category: item.category, sku: item.sku, quantity: item.quantity, unit: item.unit, min_threshold: item.min_threshold, unit_cost: item.unit_cost, batch_number: item.batch_number || "", supplier_or_client: item.supplier_or_client || "", archived: !item.archived };
          await axios.put(`${API}/inventory/${item.id}`, payload);
          toast.success(item.archived ? "Item restored" : "Item archived");
          load();
        } catch (err) { toast.error("Could not update item"); }
      }} />}
      {section === "dispatch" && <DispatchView challans={challans} inventory={activeInventory} canEdit={canEdit} onCreate={() => setModal("challan")} onPrint={setPrintChallan} onDelete={(c) => setConfirmAction({ type: "challan", item: c })} />}
      {section === "activity" && <ActivityView transactions={transactions} />}
      {section === "kitchen" && <KitchenRequisitionsView rawMaterialItems={rawMaterialItems} requisitions={requisitions} onSaved={load} canEdit={canEdit} onDelete={(r) => setConfirmAction({ type: "requisition", item: r })} />}
      {section === "trends" && <TrendsView />}
      {section === "reports" && <ReportsView lowStock={lowStock} />}
      {section === "settings" && <SettingsView business={business} onSaved={load} canEdit={canEdit} auth={auth} />}
    </main>
    {modal === "new-item" && <ItemModal onClose={() => setModal(null)} onSaved={load} />}
    {modal === "edit-item" && selected && <ItemModal item={selected} onClose={() => setModal(null)} onSaved={load} />}
    {modal === "stock" && <StockModal item={selected} onClose={() => setModal(null)} onSaved={load} />}
    {modal === "challan" && <ChallanModal inventory={activeInventory} contacts={contacts} onClose={() => setModal(null)} onSaved={load} />}
    {modal === "import" && <ImportModal onClose={() => setModal(null)} onSaved={load} />}
    {printChallan && <PrintChallan challan={printChallan} business={business} onClose={() => setPrintChallan(null)} />}
    {confirmAction?.type === "item" && <ConfirmModal
      title="Delete inventory item?"
      message={`"${confirmAction.item.name}" will be permanently removed and cannot be undone.`}
      onCancel={() => setConfirmAction(null)}
      onConfirm={() => deleteItem(confirmAction.item)}
      testid="delete-item"
    />}
    {confirmAction?.type === "challan" && <ConfirmModal
      title="Delete delivery challan?"
      message={`Challan ${confirmAction.item.challan_no} will be permanently removed and cannot be undone.`}
      onCancel={() => setConfirmAction(null)}
      onConfirm={() => deleteChallan(confirmAction.item)}
      testid="delete-challan"
    />}
    {confirmAction?.type === "requisition" && <ConfirmModal
      title="Delete kitchen requisition?"
      message={`${confirmAction.item.req_no} will be permanently removed and ${confirmAction.item.quantity_issued} ${confirmAction.item.unit} will be added back to ${confirmAction.item.item_name} stock.`}
      onCancel={() => setConfirmAction(null)}
      onConfirm={() => deleteRequisition(confirmAction.item)}
      testid="delete-requisition"
    />}
  </div>;
}
